import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Mail, Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { selectIntent, skipOnboarding } from '@/api/onboarding';

type Intent = 'create_project' | 'wait_for_invite';

export function SelectIntentPage() {
  const [selectedIntent, setSelectedIntent] = useState<Intent | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleIntentSelect = (intent: Intent) => {
    setSelectedIntent(intent);
  };

  const handleContinue = async () => {
    if (!selectedIntent) return;

    setIsSubmitting(true);
    try {
      const response = await selectIntent({ intent: selectedIntent });

      if (response.success && response.redirect) {
        navigate(response.redirect);
      } else if (response.success) {
        navigate('/projects');
      } else {
        toast.error(response.error || 'Something went wrong');
      }
    } catch (error) {
      toast.error('Failed to submit your choice. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    try {
      const response = await skipOnboarding();
      if (response.success && response.redirect) {
        navigate(response.redirect);
      } else if (response.success) {
        navigate('/projects');
      } else {
        toast.error(response.error || 'Failed to skip onboarding');
      }
    } catch (error) {
      toast.error('Failed to skip onboarding. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            What are you looking to do?
          </h1>
          <p className="text-lg text-gray-600">
            Choose the option that best fits your needs
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
              1
            </div>
            <span className="text-sm font-medium text-gray-900">Setup</span>
          </div>
          <div className="w-16 h-0.5 bg-gray-300"></div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm font-semibold">
              2
            </div>
            <span className="text-sm font-medium text-gray-600">Create Project</span>
          </div>
        </div>

        {/* Intent Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Create Project Card */}
          <Card
            className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
              selectedIntent === 'create_project'
                ? 'ring-2 ring-blue-500 shadow-lg'
                : 'hover:shadow-md'
            }`}
            onClick={() => handleIntentSelect('create_project')}
          >
            <CardContent className="p-8">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl mb-6 mx-auto">
                <Rocket className="w-8 h-8 text-white" />
              </div>

              <h3 className="text-xl font-semibold text-center mb-4">
                Setup a Project
              </h3>

              <p className="text-gray-600 text-center mb-6">
                Start from scratch and build your Odoo instance
              </p>

              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Connect your GitHub repository</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Choose your Odoo version</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Deploy your first instance</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Wait for Invite Card */}
          <Card
            className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
              selectedIntent === 'wait_for_invite'
                ? 'ring-2 ring-blue-500 shadow-lg'
                : 'hover:shadow-md'
            }`}
            onClick={() => handleIntentSelect('wait_for_invite')}
          >
            <CardContent className="p-8">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-pink-500 to-rose-500 rounded-xl mb-6 mx-auto">
                <Mail className="w-8 h-8 text-white" />
              </div>

              <h3 className="text-xl font-semibold text-center mb-4">
                Expecting an Invite
              </h3>

              <p className="text-gray-600 text-center mb-6">
                Join an existing project when invited by a team member
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handleSkip}
            disabled={isSubmitting}
            className="text-gray-500 hover:text-gray-700"
          >
            Skip for now
          </Button>

          <Button
            onClick={handleContinue}
            disabled={!selectedIntent || isSubmitting}
            className="px-8 py-3 text-lg"
          >
            {isSubmitting ? (
              'Processing...'
            ) : (
              <>
                Continue
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
